/**
 * @fileoverview Badge component - Atomic UI element
 * 
 * A flexible badge component for labels, status indicators, and tags.
 * Built with Tailwind CSS and CVA for variant management.
 * 
 * @module ui/shared/atoms/Badge
 */

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../utils/cn";

/**
 * Badge variant definitions using CVA
 */
const badgeVariants = cva(
  // Base styles - combine all base classes into one string
  "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2",
  {
    variants: {
      variant: {
        default: "bg-gray-100 text-gray-800 focus:ring-gray-500",
        primary: "bg-primary-100 text-primary-800 focus:ring-primary-500",
        success: "bg-green-100 text-green-800 focus:ring-green-500",
        warning: "bg-yellow-100 text-yellow-800 focus:ring-yellow-500",
        danger: "bg-red-100 text-red-800 focus:ring-red-500",
        info: "bg-primary-100 text-primary-800 focus:ring-primary-500",
        outline: "border border-gray-300 bg-transparent text-gray-800 focus:ring-gray-500",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

/**
 * Badge component props
 */
export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  /**
   * Render as a different element
   */
  asChild?: boolean;
}

/**
 * Badge component
 * 
 * A flexible badge component for displaying labels, status, or tags.
 * 
 * @param {BadgeProps} props - Badge props
 * @returns {JSX.Element} Badge element
 * 
 * @example
 * <Badge variant="success">Active</Badge>
 * 
 * @example
 * <Badge variant="danger">Cancelled</Badge>
 */
const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  ({ className, variant, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(badgeVariants({ variant }), className)}
        {...props}
      />
    );
  }
);

Badge.displayName = "Badge";

export { Badge, badgeVariants };

